import type { Action } from '@reduxjs/toolkit';
import { useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../../configuration/setup/hooks';
import { AssetsListDetailTab } from '../containers/assets/details/types';
import {
    assetTableApplyFilterChanges,
    assetTableSearchChanged,
    assetTableSortChanges,
    changeAssetDetailsTab,
    getAssetDetailTab,
    getAssetTableSortColumn,
    getAssetTableSortDir,
    getSearchString,
    getShowActiveAssets,
    getShowArchivedAssets,
    getShowOnlyAssetsWithoutDataSource,
    openEasyOnboardingDialog,
    setSelectedAssetId,
} from '../reducers/assets/assetsSlice';
import { createSortString, extractAssetId, makeRoute, parseRoute, parseSortString } from './routeHelper';
import { useRouteState, useUpdateRoute } from './routeHooks';

const RouteUpdater = () => {
    const dispatch = useAppDispatch();
    const { search, pathname } = useLocation();

    const selectedAssetId = useAppSelector(state => state.assets.selectedAssetId);
    const currentSearch = useAppSelector(getSearchString);
    const selectedSortBy = useAppSelector(getAssetTableSortColumn);
    const selectedSortDir = useAppSelector(getAssetTableSortDir);
    const showActiveAssets = useAppSelector(getShowActiveAssets);
    const showArchivedAssets = useAppSelector(getShowArchivedAssets);
    let selectedStatus = 'all';
    if (showActiveAssets && !showArchivedAssets) {
        selectedStatus = 'active';
    } else if (!showActiveAssets && showArchivedAssets) {
        selectedStatus = 'archived';
    } else if (!showActiveAssets && !showArchivedAssets) {
        selectedStatus = 'none';
    }
    const selectedWithoutDataSource = useAppSelector(getShowOnlyAssetsWithoutDataSource);
    const currentShowEasyOnboardingDialog = useAppSelector(state => state.assets.easyOnboardingDialog.show);

    const selectedTab = useAppSelector(getAssetDetailTab);
    const routeSearchParams = parseRoute(search);
    const shouldShowEasyOnboardingDialog =
        currentShowEasyOnboardingDialog || routeSearchParams.createAssetModal === 'show';

    // Parse initial route or after it has changed by browser navigation or user input
    useRouteState(() => {
        const dispatchQue: Action[] = [];

        const assetId = extractAssetId(pathname);
        const { status, tab, withoutDataSource, sort, q, createAssetModal } = routeSearchParams;

        if (q && q !== currentSearch) {
            dispatchQue.push(assetTableSearchChanged(q));
        }

        const parsedSort = sort ? parseSortString(sort) : undefined;

        if (parsedSort && (parsedSort.sortBy !== selectedSortBy || parsedSort.sortDir !== selectedSortDir)) {
            dispatchQue.push(assetTableSortChanges(parsedSort));
        }

        if (tab && tab !== selectedTab) {
            if (tab === 'details') {
                dispatchQue.push(changeAssetDetailsTab(AssetsListDetailTab.ASSET_DETAILS));
            } else if (tab === 'group') {
                dispatchQue.push(changeAssetDetailsTab(AssetsListDetailTab.TAG_LIST));
            } else if (tab === 'device') {
                dispatchQue.push(changeAssetDetailsTab(AssetsListDetailTab.DEVICE_LIST));
            }
        }

        if ((status && status !== selectedStatus) || (withoutDataSource === 'true') !== selectedWithoutDataSource) {
            const showOnlyAssetsWithoutDataSources = withoutDataSource === 'true';
            const showActiveAssets = status === 'all' || status === 'active';
            const showArchivedAssets = status === 'all' || status === 'archived';

            dispatchQue.push(
                assetTableApplyFilterChanges({
                    showOnlyAssetsWithoutDataSources,
                    showActiveAssets,
                    showArchivedAssets,
                })
            );
        }

        if (createAssetModal === 'show' && !currentShowEasyOnboardingDialog) {
            dispatchQue.push(openEasyOnboardingDialog());
        }

        if (assetId && assetId !== selectedAssetId) {
            dispatchQue.push(setSelectedAssetId(assetId));
        }

        for (const action of dispatchQue) {
            dispatch(action);
        }
    });

    // Update route whenever an observed store prop changes.
    // The key of the RouteState is the search param key in the URL.
    const newRoute = makeRoute({
        status: selectedStatus,
        tab: selectedTab,
        withoutDataSource: selectedWithoutDataSource ? 'true' : undefined,
        sort: createSortString({ sortBy: selectedSortBy, sortDir: selectedSortDir }),
        q: encodeURIComponent(currentSearch),
        createAssetModal: shouldShowEasyOnboardingDialog ? 'show' : undefined,
        step: shouldShowEasyOnboardingDialog ? routeSearchParams.step : undefined,
        vehicle_type: shouldShowEasyOnboardingDialog ? routeSearchParams.vehicle_type : undefined,
        brand: shouldShowEasyOnboardingDialog ? routeSearchParams.brand : undefined,
        device_type: shouldShowEasyOnboardingDialog ? routeSearchParams.device_type : undefined,
    });

    useUpdateRoute({ newSubResource: selectedAssetId ?? undefined, newSearch: newRoute });

    return null;
};

export default RouteUpdater;
